'use client'
import { FC, useState, useEffect, useMemo } from "react";
import { Content } from "@prismicio/client";
import { SliceComponentProps, PrismicRichText } from "@prismicio/react";
import Link from 'next/link';
import Tick from '../../assets/images/icons/black-tick.svg';
import ChevronDown from '../../assets/images/icons/icon-chevron-down.svg';

/**
 * Props for `ChooseAProvider`.
 */
export type ChooseAProviderProps =
  SliceComponentProps<Content.ChooseAProviderSlice>;

/**
 * Component for "ChooseAProvider" Slices.
 */
const ChooseAProvider: FC<ChooseAProviderProps> = ({ slice }) => {
  // Sorting direction for Provider column (0: Provider)
  const [providerSortAsc, setProviderSortAsc] = useState(true);

  const [selectedHeaderIdx, setSelectedHeaderIdx] = useState<number | null>(null);
  // filterType: null = all, 'business' = businessColIdx, 'residential' = residentialColIdx
  const [filterType, setFilterType] = useState<'business' | 'residential' | 'digital-inclusion' | null>(null);

  const allHeaderCells = slice.primary.providers?.head?.rows?.[0]?.cells || [];
  // Only show Provider, Contact, Fibre, Hyperfibre columns
  // Assume columns: 0 = Provider, 1 = Contact, 2 = Fibre, 3 = Hyperfibre
  const visibleColIdxs = [0, 1, 2, 3];
  const headerCells = visibleColIdxs.map(idx => allHeaderCells[idx]).filter(Boolean);
  // Shuffle rows on initial load
  const [shuffledRows, setShuffledRows] = useState<any[]>([]);

  
  
  useEffect(() => {
    // Helper to get body rows and prefix keys for uniqueness
    const getBodyRows = (provider: any, prefix: string) => {
      if (!provider?.body?.rows) return [];
      return provider.body.rows.map((row: any) => ({
        ...row,
        key: `${prefix}${row.key}`,
      }));
    };

    // Collect all body rows from all providers, prefixing keys
    const rows = [
      ...getBodyRows(slice.primary.providers, 'p1-'),
      ...getBodyRows(slice.primary.providers_02, 'p2-'),
      ...getBodyRows(slice.primary.providers_03, 'p3-'),
    ];

    // Shuffle combined rows
    const arr = [...rows];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    setShuffledRows(arr);
  }, [
    slice.primary.providers,
    slice.primary.providers_02,
    slice.primary.providers_03,
    slice.primary.providers?.body?.rows,
    slice.primary.providers_02?.body?.rows,
    slice.primary.providers_03?.body?.rows
  ]);

  const bodyRows = shuffledRows;

  console.log(bodyRows)

  // Business and Residential columns (can be in two possible places each)
  const digitalInclusionColIdx = allHeaderCells.length - 1;

  // Filter or sort rows based on selected header or filter button
  const filteredRows = useMemo(() => {
    const residentialColIdxs = [allHeaderCells.length - 5, allHeaderCells.length - 4];
    let baseRows = bodyRows;
    if (filterType === 'business') {
      // Only show rows where business fibre (col 4) or business hyperfibre (col 5) is 'Y'
      baseRows = bodyRows.filter(row => {
        const busFibre = row.cells[4]?.content?.[0]?.type === 'paragraph' && row.cells[4]?.content?.[0]?.text?.trim().toUpperCase() === 'Y';
        const busHyper = row.cells[5]?.content?.[0]?.type === 'paragraph' && row.cells[5]?.content?.[0]?.text?.trim().toUpperCase() === 'Y';
        return busFibre || busHyper;
      });
    } else if (filterType === 'residential') {
      baseRows = bodyRows.filter(row => {
        // Check both possible residential columns
        return residentialColIdxs.some(idx => {
          const cell = row.cells[idx];
          if (!cell || !cell.content || !Array.isArray(cell.content)) return false;
          const para = cell.content.find((block: { type: string }) => block.type === 'paragraph');
          const text = para && 'text' in para ? para.text : '';
          return text && text.trim().toUpperCase() === 'Y';
        });
      });
    } else if (filterType === 'digital-inclusion') {
      baseRows = bodyRows.filter(row => {
        const cell = row.cells[digitalInclusionColIdx];
        if (!cell || !cell.content || !Array.isArray(cell.content)) return false;
        const para = cell.content.find((block: { type: string }) => block.type === 'paragraph');
        const text = para && 'text' in para ? para.text : '';
        return text && text.trim().toUpperCase() === 'Y';
      });
    }


    // Always allow alpha sort for Provider (col 0)
    if (selectedHeaderIdx !== null && selectedHeaderIdx === 0) {
      const sorted = [...baseRows].sort((a, b) => {
        const getText = (row: { cells: any[] }) => {
          const cell = row.cells[0];
          if (!cell || !cell.content || !Array.isArray(cell.content)) return '';
          const para = cell.content.find((block: { type: string }) => block.type === 'paragraph');
          const text = para && 'text' in para ? para.text : '';
          return text ? text.trim().toLowerCase() : '';
        };
        const cmp = getText(a).localeCompare(getText(b));
        return providerSortAsc ? cmp : -cmp;
      });
      return sorted;
    }

    // Sub-filtering: only allow columns 2 to N-3 (index 2 to headerCells.length-1) when filterType is set
    if (filterType !== null && selectedHeaderIdx !== null && selectedHeaderIdx >= 2 && selectedHeaderIdx < headerCells.length) {
      return baseRows.filter(row => {
        const cell = row.cells[selectedHeaderIdx];
        if (!cell || !cell.content || !Array.isArray(cell.content)) return false;
        const para = cell.content.find((block: { type: string }) => block.type === 'paragraph');
        const text = para && 'text' in para ? para.text : '';
        return text && text.trim().toUpperCase() === 'Y';
      });
    }

    // If no filterType, allow filtering by header (columns 2+)
    if (filterType === null && selectedHeaderIdx !== null) {
      return baseRows.filter(row => {
        const cell = row.cells[selectedHeaderIdx];
        if (!cell || !cell.content || !Array.isArray(cell.content)) return false;
        const para = cell.content.find((block: { type: string }) => block.type === 'paragraph');
        const text = para && 'text' in para ? para.text : '';
        return text && text.trim().toUpperCase() === 'Y';
      });
    }

    // Remove rows where all visible cells are empty (no paragraph text or only whitespace)
    return baseRows.filter((row: any) => {
      // Only check visible cells (slice(0, -2))
      return row.cells.slice(0, -2).some((cell: any) => {
        const para = cell.content?.find((block: { type: string }) => block.type === 'paragraph');
        const text = para && 'text' in para ? para.text : '';
        return text && text.trim() !== '';
      });
    });
  }, [selectedHeaderIdx, filterType, bodyRows, headerCells.length, allHeaderCells.length, digitalInclusionColIdx, providerSortAsc]);

  return (
    <section  data-slice-type={slice.slice_type} 
              data-slice-variation={slice.variation}
              className="font-fellix text-jade w-full px-4 md:px-8 lg:px-12">
      <div className='max-w-[850px] mx-auto py-12'>
        <h3 className="text-[28px] leading-8 max-w-none font-semibold">{slice.primary.heading}</h3>
        <p className="text-[18px] leading-6 mt-4">Please note that fibre may not be available at all addresses within certain areas. We recommend using our <Link href="/address-checker"><span style={{ color: '#3E68FF', cursor: 'pointer' }}>address checker</span></Link> to confirm availability at your location</p>
        <div className="flex items-center gap-8 mt-6 mb-2">
          <div className="flex items-center bg-gray-200 rounded-full p-1">
            <button
              className={`px-5 py-1 rounded-full ${filterType === null ? 'bg-orange text-white' : 'bg-gray-200 text-jade'}`}
              onClick={() => {
                setFilterType(null);
                setSelectedHeaderIdx(null);
              }}
            >
              All
            </button>
            <button
              className={`px-5 py-1 rounded-full ${filterType === 'business' ? 'bg-orange text-white' : 'bg-gray-200 text-jade'}`}
              onClick={() => {
                setFilterType(filterType === 'business' ? null : 'business');
                setSelectedHeaderIdx(null);
              }}
            >
              Business
            </button>
            <button
              className={`px-5 py-1 rounded-full ${filterType === 'residential' ? 'bg-orange text-white' : 'bg-gray-200 text-jade'}`}
              onClick={() => {
                setFilterType(filterType === 'residential' ? null : 'residential');
                setSelectedHeaderIdx(null);
              }}
            >
              Residential
            </button>
             <button
              className={`px-5 py-1 rounded-full ${filterType === 'digital-inclusion' ? 'bg-orange text-white' : 'bg-gray-200 text-jade'}`}
              onClick={() => {
                setFilterType(filterType === 'digital-inclusion' ? null : 'digital-inclusion');
                setSelectedHeaderIdx(null);
              }}
            >
              Digital Inclusion
            </button>
          </div>
        </div>
        <div className='overflow-x-scroll text-[18px] w-full mt-4 pb-4 scrollbar-hide'>
          <table className="min-w-[800px] md:min-w-full border border-gray-200 rounded-lg">
            <thead className="bg-gray-100 py-2">
              <tr>
                {headerCells.map((cell: any, idx: number) => {
                  // Only allow sub-filtering for columns 2 and 3 (Fibre, Hyperfibre) when filterType is set
                  const isSubFilterable = filterType !== null && idx >= 2 && idx < headerCells.length;
                  const isHeaderFilterable = filterType === null;
                  let headerLabel = null;
                  if (idx === 2) headerLabel = 'Fibre';
                  if (idx === 3) headerLabel = 'Hyper Fibre';
                  return (
                    <th
                      key={cell?.key || idx}
                      className="px-2 py-2"
                      onClick={() => {
                        if (idx === 0) {
                          // Toggle sort direction if already selected, else select and default to asc
                          if (selectedHeaderIdx === 0) {
                            setProviderSortAsc((asc) => !asc);
                          } else {
                            setSelectedHeaderIdx(0);
                            setProviderSortAsc(true);
                          }
                        } else if (isSubFilterable || isHeaderFilterable) {
                          setSelectedHeaderIdx(selectedHeaderIdx === idx ? null : idx);
                        }
                      }}
                    >
                      <article className={`relative px-2 py-1 text-[16px] font-semibold border-b border-gray-200 select-none ${
                        (isSubFilterable || isHeaderFilterable || idx === 0) ? 'cursor-pointer' : 'cursor-default'
                      } ${selectedHeaderIdx === idx && idx > 1 ? 'bg-orange rounded-full text-white' : ''} ${idx > 1 ? 'text-center' : 'text-left'}`}>
                        {headerLabel ? (
                          <span>{headerLabel}</span>
                        ) : (
                          <PrismicRichText field={cell?.content} />
                        )}
                        {idx === 0 && (
                          <span className="absolute right-0 -top-[1px] inline-block ml-2 align-middle">
                            <ChevronDown className={`${selectedHeaderIdx === 0 && providerSortAsc ? "rotate-180" : ""} stroke-jade inline-block w-4 h-4 relative top-[2px]`} />
                            <span className="sr-only">Sort</span>
                          </span>
                        )}
                      </article>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row: any, index: number) => (
                <tr key={index} className="hover:bg-gray-50">
                  {visibleColIdxs.map((colIdx: any, idx: number) => {
                    const cell = row.cells[colIdx];
                    let showTick = false;
                    
                    // For business filter, show tick in Fibre (colIdx 2) if business fibre (col 4) is 'Y', and in Hyperfibre (colIdx 3) if business hyperfibre (col 5) is 'Y'
                    if (filterType === 'business') {
                      if (colIdx === 2) {
                        const busFibre = row.cells[4]?.content?.[0]?.type === 'paragraph' && row.cells[4]?.content?.[0]?.text?.trim().toUpperCase() === 'Y';
                        showTick = busFibre;
                        // For filtered columns, only show tick or empty - never raw content
                        return (
                          <td key={cell?.key+idx || idx} className="px-4 py-2 border-b border-gray-100 text-[14px]">
                            {showTick && <Tick className="mx-auto" />}
                          </td>
                        );
                      } else if (colIdx === 3) {
                        const busHyper = row.cells[5]?.content?.[0]?.type === 'paragraph' && row.cells[5]?.content?.[0]?.text?.trim().toUpperCase() === 'Y';
                        showTick = busHyper;
                        // For filtered columns, only show tick or empty - never raw content
                        return (
                          <td key={cell?.key+idx || idx} className="px-4 py-2 border-b border-gray-100 text-[14px]">
                            {showTick && <Tick className="mx-auto" />}
                          </td>
                        );
                      }
                    } else if (filterType === 'residential') {
                      if (colIdx === 2) {
                        const resFibre = row.cells[2]?.content?.[0]?.type === 'paragraph' && row.cells[2]?.content?.[0]?.text?.trim().toUpperCase() === 'Y';
                        showTick = resFibre;
                        // For filtered columns, only show tick or empty - never raw content
                        return (
                          <td key={cell?.key+idx || idx} className="px-4 py-2 border-b border-gray-100 text-[14px]">
                            {showTick && <Tick className="mx-auto" />}
                          </td>
                        );
                      } else if (colIdx === 3) {
                        const resHyper = row.cells[3]?.content?.[0]?.type === 'paragraph' && row.cells[3]?.content?.[0]?.text?.trim().toUpperCase() === 'Y';
                        showTick = resHyper;
                        // For filtered columns, only show tick or empty - never raw content
                        return (
                          <td key={cell?.key+idx || idx} className="px-4 py-2 border-b border-gray-100 text-[14px]">
                            {showTick && <Tick className="mx-auto" />}
                          </td>
                        );
                      }
                    } else if (filterType === 'digital-inclusion') {
                      // Fibre column (colIdx === 2): tick if column 5 or 6 is 'Y', otherwise show empty cell
                      if (colIdx === 2) {
                        const fibreA = row.cells[5]?.content?.[0]?.type === 'paragraph' && row.cells[5]?.content?.[0]?.text?.trim().toUpperCase() === 'Y';
                        const fibreB = row.cells[6]?.content?.[0]?.type === 'paragraph' && row.cells[6]?.content?.[0]?.text?.trim().toUpperCase() === 'Y';
                        showTick = fibreA || fibreB;
                        return <td key={cell?.key+idx || idx} className="px-4 py-2 border-b border-gray-100 text-[14px]">{showTick && <Tick className="mx-auto" />}</td>;
                      }
                      // Hyperfibre column (colIdx === 3): tick if column 3 or 4 is 'Y', otherwise show empty cell
                      else if (colIdx === 3) {
                        const hyperA = row.cells[3]?.content?.[0]?.type === 'paragraph' && row.cells[3]?.content?.[0]?.text?.trim().toUpperCase() === 'Y';
                        const hyperB = row.cells[4]?.content?.[0]?.type === 'paragraph' && row.cells[4]?.content?.[0]?.text?.trim().toUpperCase() === 'Y';
                        showTick = hyperA || hyperB;
                        return <td key={cell?.key+idx || idx} className="px-4 py-2 border-b border-gray-100 text-[14px]">{showTick && <Tick className="mx-auto" />}</td>;
                      }
                    }
                    else if (!filterType) {
                      // Fibre column (colIdx === 2): tick if either residential fibre (2) or business fibre (4) is 'Y'
                      if (colIdx === 2) {
                        const resFibre = row.cells[2]?.content?.[0]?.type === 'paragraph' && row.cells[2]?.content?.[0]?.text?.trim().toUpperCase() === 'Y';
                        const busFibre = row.cells[4]?.content?.[0]?.type === 'paragraph' && row.cells[4]?.content?.[0]?.text?.trim().toUpperCase() === 'Y';
                        showTick = resFibre || busFibre;
                      }
                      // Hyperfibre column (colIdx === 3): tick if either residential hyperfibre (3) or business hyperfibre (5) is 'Y'
                      else if (colIdx === 3) {
                        const resHyper = row.cells[3]?.content?.[0]?.type === 'paragraph' && row.cells[3]?.content?.[0]?.text?.trim().toUpperCase() === 'Y';
                        const busHyper = row.cells[5]?.content?.[0]?.type === 'paragraph' && row.cells[5]?.content?.[0]?.text?.trim().toUpperCase() === 'Y';
                        showTick = resHyper || busHyper;
                      }
                      // Otherwise, show tick for any cell with 'Y'
                      else if (cell?.content?.length === 1 && cell.content[0].type === 'paragraph') {
                        const para = cell.content[0];
                        const text = 'text' in para ? para.text : '';
                        if (text.trim().toUpperCase() === 'Y') showTick = true;
                      }
                    }
                    return (
                      <td
                        key={cell?.key+idx || idx}
                        className={`px-4 py-2 border-b border-gray-100 text-[14px] prose prose-a:text-blue prose-a:no-underline prose-a:hover:underline`}
                      >
                        {showTick ? (
                          <Tick className={`${idx > 1 ? "mx-auto" : "text-left"}`} />
                        ) : (
                          <PrismicRichText field={cell?.content} />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );

};
export default ChooseAProvider;
